'use client';

import { cn } from '@core/utils';
import type { ReactNode } from 'react';

import { BREAKPOINTS } from '@/constants';
import { isAdHidden, useAd } from '@/hooks/useAd';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import { AdBadge } from './AdBadge';
import {
  AD_SLOT_DEBUG_PLACEHOLDER_ENABLED,
  AdSlotDebugPlaceholder,
} from './AdSlotDebugPlaceholder';
import { getAdSlotFrameProps } from './adSlotFrame';

//* wrapperClassName이 있을 때만 외곽을 감싼다. 광고가 숨김(null)이면 wrapper도 안 생겨 빈 영역이 남지 않는다.
const SlotWrapper = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) =>
  className ? <div className={className}>{children}</div> : <>{children}</>;

interface BannerAdSlotProps {
  //* 빈 string은 콘솔 ID 미발급 슬롯 — 운영에서는 null, 개발에서는 위치 확인용 placeholder.
  unitIdM: string;
  unitIdP: string;
  mWidth: number;
  mHeight: number;
  pWidth: number;
  pHeight: number;
  rounded?: 'medium' | 'large';
  className?: string;
  //* 광고/placeholder가 실제로 렌더될 때만 감싸는 외곽 wrapper (배경·여백·높이 예약 등).
  wrapperClassName?: string;
}

export const BannerAdSlot = ({
  unitIdM,
  unitIdP,
  mWidth,
  mHeight,
  pWidth,
  pHeight,
  rounded = 'medium',
  className,
  wrapperClassName,
}: BannerAdSlotProps) => {
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP);
  const unitId = isDesktop ? unitIdP : unitIdM;

  const { containerRef, adState } = useAd(unitId, { type: 'banner' });
  const isSlotHidden = isAdHidden(adState);

  if (
    !AD_SLOT_DEBUG_PLACEHOLDER_ENABLED &&
    (isSlotHidden || (!unitIdM && !unitIdP))
  ) {
    return null;
  }

  const frame = getAdSlotFrameProps({
    unitIdM,
    unitIdP,
    mWidth,
    mHeight,
    pWidth,
    pHeight,
    showEmptyUnitPlaceholder: AD_SLOT_DEBUG_PLACEHOLDER_ENABLED,
  });

  return (
    <SlotWrapper className={wrapperClassName}>
      <div
        className={cn('relative mx-auto', frame.className, className)}
        style={frame.style}
      >
        {!unitId || isSlotHidden ? (
          AD_SLOT_DEBUG_PLACEHOLDER_ENABLED && (
            <AdSlotDebugPlaceholder rounded={rounded} className="size-full" />
          )
        ) : (
          <>
            <AdBadge
              className={cn(
                'right-12 top-12',
                adState !== 'adReceived' && 'invisible',
              )}
            />
            <div
              key={unitId}
              ref={containerRef}
              className={cn(
                'size-full overflow-hidden bg-Gray-100 [&>*]:size-full [&_iframe]:size-full',
                rounded === 'large' ? 'rounded-large' : 'rounded-medium',
                adState === 'idle' && 'invisible',
                adState === 'loading' && 'animate-pulse',
              )}
            />
          </>
        )}
      </div>
    </SlotWrapper>
  );
};
